/**
 * CoachDIA Discovery — Rappel discret ciblé "profil en attente de paiement".
 *
 * N'apparaît QUE si l'utilisateur a un profil Discovery complété mais non payé.
 * Jamais de nudge initial tant que le user n'a pas terminé le test — respect
 * strict de la règle "paiement en fin de test uniquement".
 *
 * Dismissible 24h (localStorage). Ne s'affiche pas sur /discovery ou /my-discovery.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, X, Lock } from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const API = getApiUrl();
const STORAGE_KEY = 'discovery_paywall_dismissed_at';
const EXCLUDE_PATHS = [
  '/discovery', '/my-discovery', '/login', '/signup', '/reset-password',
  '/verify-email', '/forgot-password', '/admin',
];

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function DiscoveryPaywallModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (EXCLUDE_PATHS.some(p => location.pathname.startsWith(p))) return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const ageMs = Date.now() - parseInt(dismissedAt, 10);
      if (ageMs < 24 * 3600 * 1000) return;
    }

    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/discovery/status`, { headers: authHeaders() });
        if (cancel) return;
        if (r?.data?.status === 'completed_unpaid' && r.data.has_unpaid_profile) {
          setOpen(true);
        }
      } catch (_) { /* silencieux */ }
    })();
    return () => { cancel = true; };
  }, [user, location.pathname]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
  };

  const goToMyDiscovery = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
    navigate('/my-discovery');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        data-testid="discovery-paywall-modal"
        className="max-w-lg bg-[#0F0F10] text-white border-[#92710A] rounded-sm p-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">Votre profil COACHDIA Prism est prêt</DialogTitle>
        <button
          onClick={dismiss}
          aria-label="Fermer"
          data-testid="discovery-paywall-close"
          className="absolute top-3 right-3 text-[#C4B27A] hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          <div className="text-[11px] tracking-[0.3em] text-[#92710A] uppercase mb-3 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Profil en attente de déverrouillage
          </div>
          <h2 className="font-serif text-3xl leading-tight">
            Votre rapport est prêt.<br />Plus qu'une étape.
          </h2>
          <p className="text-[#C4B27A] italic mt-2 text-sm">
            Votre profil comportemental est complété. Débloquez-le pour accéder à vos forces, angles morts et plan 30/60/90.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="discovery-paywall-cta"
              onClick={goToMyDiscovery}
              className="flex-1 bg-[#92710A] hover:bg-[#A88415] text-white rounded-full py-6 font-serif tracking-wide"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Accéder à mon profil
            </Button>
            <Button
              data-testid="discovery-paywall-later"
              onClick={dismiss}
              variant="outline"
              className="border-[#C4B27A] text-[#C4B27A] hover:bg-[#2a2a2d] hover:text-white rounded-full py-6"
            >
              Plus tard
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
